import { AssertExhaustive, Position, SCENT_TYPE_ORDER, ScentType } from './Ant';
import { Rng, Sfc32Rng } from '../Rng';

// Re-export ScentType for convenience
export { ScentType };

export enum ChamberType {
  ENTRANCE = 'entrance',
  FOOD_STORAGE = 'food_storage',
  NURSERY = 'nursery',
  QUEEN_CHAMBER = 'queen_chamber',
  MIDDEN = 'midden', // Waste/dead ant disposal
  REST_AREA = 'rest_area',
  JUNCTION = 'junction',
}

/**
 * Scent levels for a tunnel or chamber.
 * Values range 0-1 (normalized intensity).
 */
export interface ScentLevels {
  [ScentType.FOOD_TRAIL]: number;
  [ScentType.BROOD_SCENT]: number;
  [ScentType.QUEEN_SCENT]: number;
  [ScentType.ALARM]: number;
  [ScentType.NEST_ODOR]: number;
  [ScentType.FOOD_SMELL]: number;
}

/**
 * Create default (zero) scent levels.
 */
export function createDefaultScents(): ScentLevels {
  return {
    [ScentType.FOOD_TRAIL]: 0,
    [ScentType.BROOD_SCENT]: 0,
    [ScentType.QUEEN_SCENT]: 0,
    [ScentType.ALARM]: 0,
    [ScentType.NEST_ODOR]: 0,
    [ScentType.FOOD_SMELL]: 0,
  };
}

/**
 * Per-chamber stimulus values that drive ant task allocation.
 * Based on response-threshold model from ant colony research.
 * Values range from 0 to 1 (normalized).
 */
export interface ChamberStimuli {
  // Brood-related stimuli
  broodHunger: number;      // Larvae need feeding (rises with unfed larvae)
  broodRisk: number;        // Brood at risk (bad climate, overcrowding, threats)
  eggPresent: number;       // Eggs waiting to be moved

  // Food-related stimuli
  foodIncoming: number;     // Food arriving at portals (foragers returning)
  foodLow: number;          // Storage below threshold

  // Maintenance stimuli
  wasteLevel: number;       // Waste/corpse accumulation
  damageLevel: number;      // Tunnel/chamber damage nearby

  // Threat stimuli
  alarmLevel: number;       // Active threat/disturbance

  // Queen-related
  queenHunger: number;      // Queen needs feeding
}

/**
 * The stimuli, in a fixed order, for the code that has to visit all of them.
 *
 * Decay walks this list. `Object.keys(chamber.stimuli)` would walk the order the
 * object literal that built it happened to assign its fields in — which is a
 * different question from *what the stimuli are*, and only accidentally has the
 * same answer.
 *
 * The list is checked against the interface below, so a stimulus added to
 * `ChamberStimuli` and not to this list is a compile error rather than a
 * stimulus that silently stops decaying.
 */
export const STIMULUS_KEYS = Object.freeze([
  'broodHunger',
  'broodRisk',
  'eggPresent',
  'foodIncoming',
  'foodLow',
  'wasteLevel',
  'damageLevel',
  'alarmLevel',
  'queenHunger',
] as const) satisfies readonly (keyof ChamberStimuli)[];

type _AllStimuliListed = AssertExhaustive<
  Exclude<keyof ChamberStimuli, (typeof STIMULUS_KEYS)[number]>
>;

export interface Chamber {
  id: number;
  type: ChamberType;
  position: Position;
  radius: number;
  depth: number; // 0 = surface, higher = deeper
  connectedTunnels: number[];
  occupantIds: number[];
  destroyed: boolean;

  // Resource storage (any chamber can store resources)
  foodStored: number;                // Current food units stored
  foodCapacity: number;              // Maximum food this chamber can hold
  foodHue: number;                   // Accumulated color hue of stored food (0-40)
  eggCount: number;                  // Current eggs/brood in this chamber
  eggCapacity: number;               // Maximum eggs this chamber can hold

  // Scent-based navigation
  scents: ScentLevels;               // Scent levels in this chamber
  isSource: Partial<Record<ScentType, boolean>>; // Which scents this chamber emits

  // Response-threshold model additions
  stimuli: ChamberStimuli;           // Local stimulus values driving task allocation
  climateQuality: number;            // 0-1, affects brood development (1 = optimal)
  cleanliness: number;               // 0-1, affects disease risk (1 = clean)
  riskLevel: number;                 // 0-1, threats/hazards (0 = safe)
}

/**
 * Create default stimuli values for a new chamber.
 */
export function createDefaultStimuli(): ChamberStimuli {
  return {
    broodHunger: 0,
    broodRisk: 0,
    eggPresent: 0,
    foodIncoming: 0,
    foodLow: 0,
    wasteLevel: 0,
    damageLevel: 0,
    alarmLevel: 0,
    queenHunger: 0,
  };
}

export interface TunnelSegment {
  id: number;
  start: Position;
  end: Position;
  width: number;
  depth: number;
  connectedChambers: number[];
  connectedTunnels: number[];
  collapsed: boolean;
  pheromoneLevel: number; // 0-1, legacy (kept for compatibility)
  // Scent-based navigation
  scents: ScentLevels;         // Scent levels for each type
  // Congestion tracking
  length: number;              // Calculated from start/end distance
  trafficCapacity: number;     // Based on width
  currentTraffic: number;      // Ants currently traversing
}

/** What a season changes about a chamber. See `TunnelNetwork.save()`. */
export interface ChamberSnapshot {
  id: number;
  destroyed: boolean;
  occupantIds: number[];
  foodStored: number;
  foodHue: number;
  eggCount: number;
  scents: ScentLevels;
  stimuli: ChamberStimuli;
  climateQuality: number;
  cleanliness: number;
  riskLevel: number;
}

/** What a season changes about a tunnel. */
export interface TunnelSnapshot {
  id: number;
  collapsed: boolean;
  pheromoneLevel: number;
  scents: ScentLevels;
  currentTraffic: number;
}

/**
 * The dig's mutable state, as an overlay on a generated layout.
 * See `TunnelNetwork.save()`.
 */
export interface TunnelNetworkSnapshot {
  chamberCount: number;
  tunnelCount: number;
  chambers: ChamberSnapshot[];
  tunnels: TunnelSnapshot[];
}

export interface PathNode {
  position: Position;
  gCost: number;
  hCost: number;
  fCost: number;
  parent: PathNode | null;
}

export class TunnelNetwork {
  private chambers: Map<number, Chamber> = new Map();
  private tunnels: Map<number, TunnelSegment> = new Map();
  private nextChamberId = 0;
  private nextTunnelId = 0;

  // Spatial hash for efficient queries
  private spatialGrid: Map<string, (Chamber | TunnelSegment)[]> = new Map();
  private readonly GRID_SIZE = 50;

  // Colony bounds
  readonly width: number;
  readonly height: number;
  readonly maxDepth: number;

  // Seeded random for consistent colony *layout* generation. Deliberately a
  // separate stream from `simRng`: generating a bigger map must not shift the
  // runtime decisions the colony goes on to make.
  private seed: number;
  private rng: () => number;

  /**
   * Runtime randomness: collapse rolls, tunnel choice, entrance selection.
   * Injected by the owning colony so it shares that colony's stream.
   */
  private simRng: Rng;

  constructor(width = 800, height = 400, maxDepth = 5, seed = 0, simRng?: Rng) {
    this.width = width;
    this.height = height;
    this.maxDepth = maxDepth;
    this.seed = seed;
    this.rng = this.createSeededRandom(seed);
    this.simRng = simRng ?? new Sfc32Rng(`tunnel-network/${seed}`);
  }

  /** Attach the owning colony's simulation stream. */
  setSimRng(rng: Rng): void {
    this.simRng = rng;
  }

  /**
   * Create a seeded random number generator.
   * Uses a simple mulberry32 algorithm.
   */
  private createSeededRandom(seed: number): () => number {
    let state = seed;
    return () => {
      state = (state + 0x6D2B79F5) | 0;
      let t = Math.imul(state ^ (state >>> 15), 1 | state);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /**
   * Get a seeded random number in range [0, 1).
   */
  private random(): number {
    return this.rng();
  }

  /**
   * Get a seeded random number in range [min, max).
   */
  private randomRange(min: number, max: number): number {
    return min + this.random() * (max - min);
  }

  generateNetwork(): void {
    // Generate colony based on seed - each seed produces a different layout
    const layoutType = this.seed % 3;

    switch (layoutType) {
      case 0:
        this.generateClassicLayout();
        break;
      case 1:
        this.generateDeepLayout();
        break;
      case 2:
        this.generateSprawlingLayout();
        break;
      default:
        this.generateClassicLayout();
    }

    // Add random branches based on seed
    this.addRandomBranches(4 + Math.floor(this.random() * 4));
  }

  /**
   * Classic layout: Central structure with balanced chambers.
   */
  private generateClassicLayout(): void {
    // Randomize positions slightly based on seed
    const offsetX = this.randomRange(-0.05, 0.05);
    const offsetY = this.randomRange(-0.05, 0.05);

    // 1. Queen's chamber (deepest, center)
    const queenChamber = this.createChamber(
      ChamberType.QUEEN_CHAMBER,
      { x: this.width * (0.5 + offsetX), y: this.height * (0.8 + offsetY) },
      38 + this.randomRange(-3, 5),
      this.maxDepth
    );

    // 2. Main entrances
    const entrance1 = this.createChamber(
      ChamberType.ENTRANCE,
      { x: this.width * (0.3 + this.randomRange(-0.05, 0.05)), y: 25 + this.randomRange(0, 15) },
      18 + this.randomRange(0, 5),
      0
    );

    const entrance2 = this.createChamber(
      ChamberType.ENTRANCE,
      { x: this.width * (0.7 + this.randomRange(-0.05, 0.05)), y: 30 + this.randomRange(0, 15) },
      16 + this.randomRange(0, 5),
      0
    );

    // 3. Main junction
    const junction1 = this.createChamber(
      ChamberType.JUNCTION,
      { x: this.width * (0.4 + this.randomRange(-0.05, 0.05)), y: this.height * (0.3 + this.randomRange(-0.05, 0.05)) },
      22 + this.randomRange(0, 6),
      2
    );

    this.connectWithTunnel(entrance1.id, junction1.id);
    this.connectWithTunnel(entrance2.id, junction1.id);

    // 4. Nursery
    const nursery = this.createChamber(
      ChamberType.NURSERY,
      { x: this.width * (0.35 + this.randomRange(-0.05, 0.05)), y: this.height * (0.55 + this.randomRange(-0.05, 0.05)) },
      33 + this.randomRange(0, 6),
      3
    );
    this.connectWithTunnel(junction1.id, nursery.id);

    // 5. Food storage chambers
    const foodStorage1 = this.createChamber(
      ChamberType.FOOD_STORAGE,
      { x: this.width * (0.6 + this.randomRange(-0.05, 0.05)), y: this.height * (0.4 + this.randomRange(-0.05, 0.05)) },
      28 + this.randomRange(0, 5),
      2
    );
    foodStorage1.foodStored = 180 + Math.floor(this.randomRange(0, 40));

    const foodStorage2 = this.createChamber(
      ChamberType.FOOD_STORAGE,
      { x: this.width * (0.25 + this.randomRange(-0.05, 0.05)), y: this.height * (0.7 + this.randomRange(-0.05, 0.05)) },
      23 + this.randomRange(0, 5),
      4
    );
    foodStorage2.foodStored = 100 + Math.floor(this.randomRange(0, 20));

    this.connectWithTunnel(junction1.id, foodStorage1.id);
    this.connectWithTunnel(nursery.id, foodStorage2.id);

    // 6. Lower junction to queen
    const junction2 = this.createChamber(
      ChamberType.JUNCTION,
      { x: this.width * (0.5 + this.randomRange(-0.05, 0.05)), y: this.height * (0.65 + this.randomRange(-0.03, 0.03)) },
      18 + this.randomRange(0, 5),
      4
    );

    this.connectWithTunnel(nursery.id, junction2.id);
    this.connectWithTunnel(foodStorage1.id, junction2.id);
    this.connectWithTunnel(junction2.id, queenChamber.id);

    // 7. Midden
    const midden = this.createChamber(
      ChamberType.MIDDEN,
      { x: this.width * (0.85 + this.randomRange(-0.05, 0.05)), y: this.height * (0.25 + this.randomRange(-0.05, 0.05)) },
      18 + this.randomRange(0, 5),
      1
    );
    this.connectWithTunnel(entrance2.id, midden.id);

    // 8. Rest areas
    const restArea1 = this.createChamber(
      ChamberType.REST_AREA,
      { x: this.width * (0.2 + this.randomRange(-0.05, 0.05)), y: this.height * (0.45 + this.randomRange(-0.05, 0.05)) },
      20 + this.randomRange(0, 5),
      2
    );
    const restArea2 = this.createChamber(
      ChamberType.REST_AREA,
      { x: this.width * (0.7 + this.randomRange(-0.05, 0.05)), y: this.height * (0.6 + this.randomRange(-0.05, 0.05)) },
      18 + this.randomRange(0, 5),
      3
    );

    this.connectWithTunnel(junction1.id, restArea1.id);
    this.connectWithTunnel(foodStorage1.id, restArea2.id);
  }

  /**
   * Deep layout: Vertical structure with queen chamber very deep.
   */
  private generateDeepLayout(): void {
    // Queen at the very bottom center
    const queenChamber = this.createChamber(
      ChamberType.QUEEN_CHAMBER,
      { x: this.width * 0.5, y: this.height * 0.9 },
      42,
      this.maxDepth
    );

    // Single main entrance at top center
    const entrance1 = this.createChamber(
      ChamberType.ENTRANCE,
      { x: this.width * (0.45 + this.randomRange(-0.05, 0.05)), y: 20 + this.randomRange(0, 10) },
      22,
      0
    );

    const entrance2 = this.createChamber(
      ChamberType.ENTRANCE,
      { x: this.width * (0.55 + this.randomRange(-0.05, 0.05)), y: 25 + this.randomRange(0, 10) },
      18,
      0
    );

    // Vertical chain of junctions going down
    const junction1 = this.createChamber(
      ChamberType.JUNCTION,
      { x: this.width * 0.5, y: this.height * 0.2 },
      20,
      1
    );
    this.connectWithTunnel(entrance1.id, junction1.id);
    this.connectWithTunnel(entrance2.id, junction1.id);

    // Midden off to the side near top
    const midden = this.createChamber(
      ChamberType.MIDDEN,
      { x: this.width * 0.15, y: this.height * 0.15 },
      18,
      1
    );
    this.connectWithTunnel(junction1.id, midden.id);

    // Food storage level
    const foodStorage1 = this.createChamber(
      ChamberType.FOOD_STORAGE,
      { x: this.width * 0.3, y: this.height * 0.35 },
      30,
      2
    );
    foodStorage1.foodStored = 180;

    const foodStorage2 = this.createChamber(
      ChamberType.FOOD_STORAGE,
      { x: this.width * 0.7, y: this.height * 0.35 },
      28,
      2
    );
    foodStorage2.foodStored = 120;

    const junction2 = this.createChamber(
      ChamberType.JUNCTION,
      { x: this.width * 0.5, y: this.height * 0.4 },
      22,
      2
    );
    this.connectWithTunnel(junction1.id, junction2.id);
    this.connectWithTunnel(junction2.id, foodStorage1.id);
    this.connectWithTunnel(junction2.id, foodStorage2.id);

    // Rest area level
    const restArea1 = this.createChamber(
      ChamberType.REST_AREA,
      { x: this.width * 0.25, y: this.height * 0.55 },
      22,
      3
    );
    const restArea2 = this.createChamber(
      ChamberType.REST_AREA,
      { x: this.width * 0.75, y: this.height * 0.55 },
      20,
      3
    );

    const junction3 = this.createChamber(
      ChamberType.JUNCTION,
      { x: this.width * 0.5, y: this.height * 0.55 },
      18,
      3
    );
    this.connectWithTunnel(junction2.id, junction3.id);
    this.connectWithTunnel(junction3.id, restArea1.id);
    this.connectWithTunnel(junction3.id, restArea2.id);

    // Nursery level (protected, deep)
    const nursery = this.createChamber(
      ChamberType.NURSERY,
      { x: this.width * 0.5, y: this.height * 0.72 },
      38,
      4
    );
    this.connectWithTunnel(junction3.id, nursery.id);

    // Final connection to queen
    this.connectWithTunnel(nursery.id, queenChamber.id);
  }

  /**
   * Sprawling layout: Wide horizontal structure with multiple branches.
   */
  private generateSprawlingLayout(): void {
    // Queen off to one side, deep
    const queenChamber = this.createChamber(
      ChamberType.QUEEN_CHAMBER,
      { x: this.width * 0.75, y: this.height * 0.85 },
      40,
      this.maxDepth
    );

    // Multiple entrances spread across
    const entrance1 = this.createChamber(
      ChamberType.ENTRANCE,
      { x: this.width * 0.15, y: 30 },
      20,
      0
    );
    const entrance2 = this.createChamber(
      ChamberType.ENTRANCE,
      { x: this.width * 0.5, y: 25 },
      22,
      0
    );
    const entrance3 = this.createChamber(
      ChamberType.ENTRANCE,
      { x: this.width * 0.85, y: 35 },
      18,
      0
    );

    // Horizontal main corridor
    const junction1 = this.createChamber(
      ChamberType.JUNCTION,
      { x: this.width * 0.2, y: this.height * 0.25 },
      22,
      1
    );
    const junction2 = this.createChamber(
      ChamberType.JUNCTION,
      { x: this.width * 0.5, y: this.height * 0.28 },
      25,
      1
    );
    const junction3 = this.createChamber(
      ChamberType.JUNCTION,
      { x: this.width * 0.8, y: this.height * 0.25 },
      20,
      1
    );

    this.connectWithTunnel(entrance1.id, junction1.id);
    this.connectWithTunnel(entrance2.id, junction2.id);
    this.connectWithTunnel(entrance3.id, junction3.id);
    this.connectWithTunnel(junction1.id, junction2.id);
    this.connectWithTunnel(junction2.id, junction3.id);

    // Left wing - food storage
    const foodStorage1 = this.createChamber(
      ChamberType.FOOD_STORAGE,
      { x: this.width * 0.1, y: this.height * 0.5 },
      28,
      2
    );
    foodStorage1.foodStored = 150;
    this.connectWithTunnel(junction1.id, foodStorage1.id);

    // Left rest area
    const restArea1 = this.createChamber(
      ChamberType.REST_AREA,
      { x: this.width * 0.25, y: this.height * 0.6 },
      22,
      3
    );
    this.connectWithTunnel(foodStorage1.id, restArea1.id);

    // Center - nursery
    const nursery = this.createChamber(
      ChamberType.NURSERY,
      { x: this.width * 0.45, y: this.height * 0.55 },
      36,
      3
    );
    this.connectWithTunnel(junction2.id, nursery.id);

    // Midden near center entrance
    const midden = this.createChamber(
      ChamberType.MIDDEN,
      { x: this.width * 0.6, y: this.height * 0.15 },
      18,
      1
    );
    this.connectWithTunnel(junction2.id, midden.id);

    // Right wing - more food storage and path to queen
    const foodStorage2 = this.createChamber(
      ChamberType.FOOD_STORAGE,
      { x: this.width * 0.9, y: this.height * 0.45 },
      26,
      2
    );
    foodStorage2.foodStored = 150;
    this.connectWithTunnel(junction3.id, foodStorage2.id);

    const restArea2 = this.createChamber(
      ChamberType.REST_AREA,
      { x: this.width * 0.7, y: this.height * 0.55 },
      20,
      3
    );
    this.connectWithTunnel(foodStorage2.id, restArea2.id);

    // Junction leading to queen
    const junction4 = this.createChamber(
      ChamberType.JUNCTION,
      { x: this.width * 0.65, y: this.height * 0.72 },
      20,
      4
    );
    this.connectWithTunnel(nursery.id, junction4.id);
    this.connectWithTunnel(restArea2.id, junction4.id);
    this.connectWithTunnel(junction4.id, queenChamber.id);

    // Connect left side to center
    this.connectWithTunnel(restArea1.id, nursery.id);
  }

  private createChamber(
    type: ChamberType,
    position: Position,
    radius: number,
    depth: number
  ): Chamber {
    // Set climate quality based on chamber type and depth
    // Deeper chambers tend to have more stable climate
    const baseClimate = 0.7 + (depth / this.maxDepth) * 0.2;
    // Nursery and queen chamber have better climate
    const typeBonus = (type === ChamberType.NURSERY || type === ChamberType.QUEEN_CHAMBER) ? 0.1 : 0;

    // Capacity scales with chamber size (area proportional to radius squared)
    // Large chamber (radius ~50) should hold ~500 food and ~20 eggs
    // Formula: foodCapacity = radius^2 / 5, eggCapacity = radius^2 / 125
    const radiusSq = radius * radius;
    const foodCapacity = Math.max(50, Math.round(radiusSq / 5)); // Min 50, large ~500
    const eggCapacity = Math.max(3, Math.round(radiusSq / 125)); // Min 3, large ~20

    // Determine which scents this chamber emits based on type
    const isSource: Partial<Record<ScentType, boolean>> = {};
    if (type === ChamberType.QUEEN_CHAMBER) {
      isSource[ScentType.QUEEN_SCENT] = true;
    }
    if (type === ChamberType.NURSERY) {
      isSource[ScentType.BROOD_SCENT] = true;
    }
    if (type === ChamberType.FOOD_STORAGE) {
      isSource[ScentType.FOOD_SMELL] = true;
    }
    // All chambers emit NEST_ODOR proportional to depth
    isSource[ScentType.NEST_ODOR] = true;

    const chamber: Chamber = {
      id: this.nextChamberId++,
      type,
      position: { ...position },
      radius,
      depth,
      connectedTunnels: [],
      occupantIds: [],
      destroyed: false,
      // Resource storage
      foodStored: 0,
      foodCapacity,
      foodHue: 20, // Default amber color
      eggCount: 0,
      eggCapacity,
      // Scent-based navigation
      scents: createDefaultScents(),
      isSource,
      // Response-threshold model additions
      stimuli: createDefaultStimuli(),
      climateQuality: Math.min(1, baseClimate + typeBonus),
      cleanliness: type === ChamberType.MIDDEN ? 0.3 : 1.0, // Midden starts dirty
      riskLevel: type === ChamberType.ENTRANCE ? 0.2 : 0, // Entrances have some risk
    };

    this.chambers.set(chamber.id, chamber);
    this.addToSpatialGrid(chamber);

    return chamber;
  }

  private connectWithTunnel(chamberId1: number, chamberId2: number): TunnelSegment | null {
    const chamber1 = this.chambers.get(chamberId1);
    const chamber2 = this.chambers.get(chamberId2);

    if (!chamber1 || !chamber2) return null;

    // Calculate tunnel length
    const dx = chamber2.position.x - chamber1.position.x;
    const dy = chamber2.position.y - chamber1.position.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    const width = 8 + this.random() * 4;

    const tunnel: TunnelSegment = {
      id: this.nextTunnelId++,
      start: { ...chamber1.position },
      end: { ...chamber2.position },
      width,
      depth: (chamber1.depth + chamber2.depth) / 2,
      connectedChambers: [chamberId1, chamberId2],
      connectedTunnels: [],
      collapsed: false,
      pheromoneLevel: 0, // Legacy, kept for compatibility
      // Scent-based navigation
      scents: createDefaultScents(),
      // Congestion tracking
      length,
      trafficCapacity: Math.floor(width * 2), // Wider tunnels handle more traffic
      currentTraffic: 0,
    };

    this.tunnels.set(tunnel.id, tunnel);
    chamber1.connectedTunnels.push(tunnel.id);
    chamber2.connectedTunnels.push(tunnel.id);

    this.addToSpatialGrid(tunnel);

    return tunnel;
  }

  private addRandomBranches(count: number): void {
    const chamberArray = Array.from(this.chambers.values());

    for (let i = 0; i < count; i++) {
      // Pick a random chamber to branch from (using seeded random)
      const source = chamberArray[Math.floor(this.random() * chamberArray.length)];

      // Create a small dead-end or junction
      const angle = this.random() * Math.PI * 2;
      const distance = 50 + this.random() * 80;
      const newPos = {
        x: Math.max(50, Math.min(this.width - 50, source.position.x + Math.cos(angle) * distance)),
        y: Math.max(50, Math.min(this.height - 50, source.position.y + Math.sin(angle) * distance)),
      };

      const newChamber = this.createChamber(
        ChamberType.JUNCTION,
        newPos,
        12 + this.random() * 10,
        source.depth + (this.random() > 0.5 ? 1 : 0)
      );

      this.connectWithTunnel(source.id, newChamber.id);
    }
  }

  private getSpatialKey(x: number, y: number): string {
    const gridX = Math.floor(x / this.GRID_SIZE);
    const gridY = Math.floor(y / this.GRID_SIZE);
    return `${gridX},${gridY}`;
  }

  private addToSpatialGrid(item: Chamber | TunnelSegment): void {
    const pos = 'position' in item ? item.position : item.start;
    const key = this.getSpatialKey(pos.x, pos.y);

    if (!this.spatialGrid.has(key)) {
      this.spatialGrid.set(key, []);
    }
    this.spatialGrid.get(key)!.push(item);
  }

  // Find chambers within a radius
  getChambersNear(position: Position, radius: number): Chamber[] {
    const result: Chamber[] = [];

    for (const chamber of this.chambers.values()) {
      if (chamber.destroyed) continue;

      const dx = chamber.position.x - position.x;
      const dy = chamber.position.y - position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= radius + chamber.radius) {
        result.push(chamber);
      }
    }

    return result;
  }

  // Get all entrance chambers
  getEntrances(): Chamber[] {
    return Array.from(this.chambers.values()).filter(
      (c) => c.type === ChamberType.ENTRANCE && !c.destroyed
    );
  }

  // Get queen's chamber
  getQueenChamber(): Chamber | undefined {
    return Array.from(this.chambers.values()).find(
      (c) => c.type === ChamberType.QUEEN_CHAMBER && !c.destroyed
    );
  }

  // Get nursery
  getNursery(): Chamber | undefined {
    return Array.from(this.chambers.values()).find(
      (c) => c.type === ChamberType.NURSERY && !c.destroyed
    );
  }

  // Get food storages
  getFoodStorages(): Chamber[] {
    return Array.from(this.chambers.values()).filter(
      (c) => c.type === ChamberType.FOOD_STORAGE && !c.destroyed
    );
  }

  /**
   * Apply damage to tunnels/chambers in an area with distance-based falloff.
   * @param position - Center of damage
   * @param coreRadius - Radius for guaranteed damage (100% chance)
   * @param maxRadius - Maximum radius for any damage (0% chance at edge)
   * @param severity - Base severity multiplier (0-1, affects damage probability)
   */
  collapseArea(
    position: Position,
    coreRadius: number,
    maxRadius: number = coreRadius * 3,
    severity: number = 1.0
  ): { tunnels: number; chambers: number; antsAffected: number[] } {
    const result = { tunnels: 0, chambers: 0, antsAffected: [] as number[] };

    // Check chambers
    for (const chamber of this.chambers.values()) {
      if (chamber.destroyed) continue;

      const dx = chamber.position.x - position.x;
      const dy = chamber.position.y - position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > maxRadius) continue;

      // Calculate damage probability based on distance
      // 100% at dist=0, falls off linearly to 0% at maxRadius
      // Core radius gets guaranteed damage
      let damageProb: number;
      if (dist <= coreRadius) {
        damageProb = 1.0;
      } else {
        // Linear falloff from coreRadius to maxRadius
        damageProb = 1.0 - (dist - coreRadius) / (maxRadius - coreRadius);
      }
      damageProb *= severity;

      if (this.simRng.chance(damageProb)) {
        chamber.destroyed = true;
        result.chambers++;
        result.antsAffected.push(...chamber.occupantIds);
      }
    }

    // Check tunnels
    for (const tunnel of this.tunnels.values()) {
      if (tunnel.collapsed) continue;

      // Check if tunnel passes through the collapse area
      const midX = (tunnel.start.x + tunnel.end.x) / 2;
      const midY = (tunnel.start.y + tunnel.end.y) / 2;
      const dx = midX - position.x;
      const dy = midY - position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > maxRadius) continue;

      // Calculate damage probability
      let damageProb: number;
      if (dist <= coreRadius) {
        damageProb = 1.0;
      } else {
        damageProb = 1.0 - (dist - coreRadius) / (maxRadius - coreRadius);
      }
      damageProb *= severity;

      if (this.simRng.chance(damageProb)) {
        tunnel.collapsed = true;
        result.tunnels++;
      }
    }

    return result;
  }

  // Get total food stored
  getTotalFood(): number {
    let total = 0;
    for (const chamber of this.chambers.values()) {
      if (!chamber.destroyed && chamber.foodStored) {
        total += chamber.foodStored;
      }
    }
    return total;
  }

  // Calculate territory (non-destroyed chambers)
  getTerritoryPercentage(): number {
    const total = this.chambers.size;
    const active = Array.from(this.chambers.values()).filter((c) => !c.destroyed).length;
    return total > 0 ? active / total : 0;
  }

  getAllChambers(): Chamber[] {
    return Array.from(this.chambers.values());
  }

  getAllTunnels(): TunnelSegment[] {
    return Array.from(this.tunnels.values());
  }

  getChamber(id: number): Chamber | undefined {
    return this.chambers.get(id);
  }

  getTunnel(id: number): TunnelSegment | undefined {
    return this.tunnels.get(id);
  }

  /**
   * Get chambers connected to a given chamber via non-collapsed tunnels.
   */
  getNeighborChambers(chamberId: number): number[] {
    const chamber = this.chambers.get(chamberId);
    if (!chamber || chamber.destroyed) return [];

    const neighbors: number[] = [];

    for (const tunnelId of chamber.connectedTunnels) {
      const tunnel = this.tunnels.get(tunnelId);
      if (!tunnel || tunnel.collapsed) continue;

      // Get the other chamber connected by this tunnel
      for (const connectedChamberId of tunnel.connectedChambers) {
        if (connectedChamberId !== chamberId) {
          const otherChamber = this.chambers.get(connectedChamberId);
          if (otherChamber && !otherChamber.destroyed) {
            neighbors.push(connectedChamberId);
          }
        }
      }
    }

    return neighbors;
  }

  /**
   * Get the tunnel connecting two chambers, if any.
   */
  getTunnelBetween(chamber1Id: number, chamber2Id: number): TunnelSegment | null {
    const chamber1 = this.chambers.get(chamber1Id);
    if (!chamber1) return null;

    for (const tunnelId of chamber1.connectedTunnels) {
      const tunnel = this.tunnels.get(tunnelId);
      if (!tunnel || tunnel.collapsed) continue;

      if (tunnel.connectedChambers.includes(chamber2Id)) {
        return tunnel;
      }
    }

    return null;
  }

  /**
   * Calculate the distance between two positions.
   */
  private distance(a: Position, b: Position): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Find a path from one chamber to another using A* pathfinding.
   * Returns an array of chamber IDs to traverse, or null if no path exists.
   * The path includes both start and end chambers.
   */
  findPath(fromChamberId: number, toChamberId: number): number[] | null {
    const startChamber = this.chambers.get(fromChamberId);
    const endChamber = this.chambers.get(toChamberId);

    if (!startChamber || !endChamber) return null;
    if (startChamber.destroyed || endChamber.destroyed) return null;
    if (fromChamberId === toChamberId) return [fromChamberId];

    // A* implementation
    const openSet = new Set<number>([fromChamberId]);
    const cameFrom = new Map<number, number>();
    const gScore = new Map<number, number>();
    const fScore = new Map<number, number>();

    gScore.set(fromChamberId, 0);
    fScore.set(fromChamberId, this.distance(startChamber.position, endChamber.position));

    while (openSet.size > 0) {
      // Find node in openSet with lowest fScore
      let current: number | null = null;
      let lowestF = Infinity;

      for (const nodeId of openSet) {
        const f = fScore.get(nodeId) ?? Infinity;
        if (f < lowestF) {
          lowestF = f;
          current = nodeId;
        }
      }

      if (current === null) break;

      // Check if we've reached the goal
      if (current === toChamberId) {
        // Reconstruct path
        const path: number[] = [current];
        while (cameFrom.has(current)) {
          current = cameFrom.get(current)!;
          path.unshift(current);
        }
        return path;
      }

      openSet.delete(current);
      const currentChamber = this.chambers.get(current)!;

      // Check all neighbors
      for (const neighborId of this.getNeighborChambers(current)) {
        const neighborChamber = this.chambers.get(neighborId);
        if (!neighborChamber) continue;

        // Calculate edge weight (tunnel distance with pheromone bonus)
        const tunnel = this.getTunnelBetween(current, neighborId);
        let edgeWeight = this.distance(currentChamber.position, neighborChamber.position);

        // Apply pheromone bonus - higher pheromone = lower weight
        if (tunnel) {
          // Weight modifier: 1.1 for no pheromone, 0.9 for max pheromone
          edgeWeight *= (1.1 - tunnel.pheromoneLevel * 0.2);
        }

        const tentativeG = (gScore.get(current) ?? Infinity) + edgeWeight;

        if (tentativeG < (gScore.get(neighborId) ?? Infinity)) {
          cameFrom.set(neighborId, current);
          gScore.set(neighborId, tentativeG);
          fScore.set(neighborId, tentativeG + this.distance(neighborChamber.position, endChamber.position));

          if (!openSet.has(neighborId)) {
            openSet.add(neighborId);
          }
        }
      }
    }

    // No path found
    return null;
  }

  /**
   * Convert a chamber path to a series of waypoints for smooth movement.
   * Returns positions along tunnels between chambers.
   */
  getWaypointsForPath(chamberPath: number[]): Position[] {
    if (chamberPath.length === 0) return [];

    const waypoints: Position[] = [];

    for (let i = 0; i < chamberPath.length; i++) {
      const chamber = this.chambers.get(chamberPath[i]);
      if (!chamber) continue;

      // Add chamber center as waypoint
      waypoints.push({ ...chamber.position });

      // If there's a next chamber, add intermediate tunnel waypoints
      if (i < chamberPath.length - 1) {
        const nextChamber = this.chambers.get(chamberPath[i + 1]);
        if (nextChamber) {
          // Add a midpoint along the tunnel for smoother movement
          const midpoint: Position = {
            x: (chamber.position.x + nextChamber.position.x) / 2,
            y: (chamber.position.y + nextChamber.position.y) / 2,
          };
          waypoints.push(midpoint);
        }
      }
    }

    return waypoints;
  }

  /**
   * Find the nearest chamber of a specific type.
   */
  findNearestChamberOfType(fromPos: Position, type: ChamberType): Chamber | null {
    let nearest: Chamber | null = null;
    let nearestDist = Infinity;

    for (const chamber of this.chambers.values()) {
      if (chamber.destroyed || chamber.type !== type) continue;

      const dist = this.distance(fromPos, chamber.position);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = chamber;
      }
    }

    return nearest;
  }

  /**
   * Find a random entrance chamber.
   */
  getRandomEntrance(): Chamber | null {
    const entrances = this.getEntrances();
    if (entrances.length === 0) return null;
    return this.simRng.pick(entrances)!;
  }

  /**
   * Find chamber containing a position.
   */
  getChamberAt(pos: Position): Chamber | null {
    for (const chamber of this.chambers.values()) {
      if (chamber.destroyed) continue;

      const dx = pos.x - chamber.position.x;
      const dy = pos.y - chamber.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= chamber.radius) {
        return chamber;
      }
    }
    return null;
  }

  /**
   * Find a storage chamber with available capacity.
   */
  getStorageWithSpace(maxCapacity = 3000): Chamber | null {
    const storages = this.getFoodStorages();
    for (const storage of storages) {
      const currentFood = storage.foodStored ?? 0;
      if (currentFood < maxCapacity) {
        return storage;
      }
    }
    return null;
  }

  /**
   * Find a storage chamber (FOOD_STORAGE type) with food.
   */
  getStorageWithFood(): Chamber | null {
    const storages = this.getFoodStorages();
    for (const storage of storages) {
      const currentFood = storage.foodStored ?? 0;
      if (currentFood > 0) {
        return storage;
      }
    }
    return null;
  }

  /**
   * Find ANY chamber with food (not just FOOD_STORAGE type).
   * This is important because food can be stored in any chamber.
   */
  getAnyStorageWithFood(): Chamber | null {
    // First try dedicated food storage chambers
    const dedicated = this.getStorageWithFood();
    if (dedicated) return dedicated;

    // Then check any other chamber with food
    for (const chamber of this.chambers.values()) {
      if (chamber.destroyed) continue;
      const currentFood = chamber.foodStored ?? 0;
      if (currentFood > 0) {
        return chamber;
      }
    }
    return null;
  }

  /**
   * Deposit pheromone along a tunnel.
   */
  depositPheromone(tunnelId: number, amount: number): void {
    const tunnel = this.tunnels.get(tunnelId);
    if (tunnel && !tunnel.collapsed) {
      tunnel.pheromoneLevel = Math.min(1, tunnel.pheromoneLevel + amount);
    }
  }

  /**
   * Decay pheromones on all tunnels.
   */
  decayPheromones(decayRate: number): void {
    for (const tunnel of this.tunnels.values()) {
      if (tunnel.pheromoneLevel > 0) {
        tunnel.pheromoneLevel = Math.max(0, tunnel.pheromoneLevel - decayRate);
      }
    }
  }

  // ======== Scent-Based Navigation System ========

  /**
   * Update all scents: emit from sources, diffuse through network, decay.
   * Call this each frame from FormicariumLayer.
   */
  updateScents(dt: number): void {
    const DIFFUSION_RATE = 0.15;   // How fast scents spread per second
    const DECAY_RATE = 0.05;       // How fast scents fade per second
    const dtSeconds = dt / 1000;

    // 1. Emit scents from source chambers
    for (const chamber of this.chambers.values()) {
      if (chamber.destroyed) continue;

      // Queen chamber emits QUEEN_SCENT (constant beacon)
      if (chamber.isSource[ScentType.QUEEN_SCENT]) {
        chamber.scents[ScentType.QUEEN_SCENT] = 1.0;
      }

      // ANY chamber with brood emits BROOD_SCENT proportional to brood count
      // This is biologically plausible - scent comes from brood, not chamber type
      if (chamber.eggCount > 0) {
        const broodIntensity = Math.min(1, chamber.eggCount / Math.max(1, chamber.eggCapacity));
        chamber.scents[ScentType.BROOD_SCENT] = Math.max(
          chamber.scents[ScentType.BROOD_SCENT],
          0.2 + broodIntensity * 0.6  // Slightly weaker than before to encourage clustering
        );
      }

      // Food storage emits FOOD_SMELL proportional to food stored
      if (chamber.foodStored > 0) {
        const foodIntensity = Math.min(1, chamber.foodStored / Math.max(1, chamber.foodCapacity));
        chamber.scents[ScentType.FOOD_SMELL] = Math.max(
          chamber.scents[ScentType.FOOD_SMELL],
          foodIntensity * 0.8
        );
      }

      // All chambers emit NEST_ODOR based on depth (deeper = stronger)
      if (chamber.isSource[ScentType.NEST_ODOR]) {
        const depthIntensity = chamber.depth / this.maxDepth;
        chamber.scents[ScentType.NEST_ODOR] = Math.max(
          chamber.scents[ScentType.NEST_ODOR],
          0.2 + depthIntensity * 0.6
        );
      }
    }

    // 2. Diffuse scents from chambers to connected tunnels
    for (const tunnel of this.tunnels.values()) {
      if (tunnel.collapsed) continue;

      for (const chamberId of tunnel.connectedChambers) {
        const chamber = this.chambers.get(chamberId);
        if (!chamber || chamber.destroyed) continue;

        // Scents flow from high to low concentration
        for (const scentType of SCENT_TYPE_ORDER) {
          const chamberLevel = chamber.scents[scentType];
          const tunnelLevel = tunnel.scents[scentType];
          const diff = chamberLevel - tunnelLevel;

          if (diff > 0.01) {
            // Scent flows from chamber to tunnel
            const flow = diff * DIFFUSION_RATE * dtSeconds;
            tunnel.scents[scentType] = Math.min(1, tunnelLevel + flow);
          }
        }
      }
    }

    // 3. Diffuse scents from tunnels to connected chambers (weaker, for spreading)
    for (const tunnel of this.tunnels.values()) {
      if (tunnel.collapsed) continue;

      for (const chamberId of tunnel.connectedChambers) {
        const chamber = this.chambers.get(chamberId);
        if (!chamber || chamber.destroyed) continue;

        for (const scentType of SCENT_TYPE_ORDER) {
          // Skip if chamber is a source of this scent (don't override emissions)
          if (chamber.isSource[scentType]) continue;

          const tunnelLevel = tunnel.scents[scentType];
          const chamberLevel = chamber.scents[scentType];
          const diff = tunnelLevel - chamberLevel;

          if (diff > 0.01) {
            // Weaker flow from tunnel to chamber
            const flow = diff * DIFFUSION_RATE * 0.5 * dtSeconds;
            chamber.scents[scentType] = Math.min(1, chamberLevel + flow);
          }
        }
      }
    }

    // 4. Decay all scents (except at sources)
    for (const chamber of this.chambers.values()) {
      for (const scentType of SCENT_TYPE_ORDER) {
        // Don't decay scents at their source
        if (chamber.isSource[scentType]) continue;

        if (chamber.scents[scentType] > 0) {
          chamber.scents[scentType] = Math.max(0, chamber.scents[scentType] - DECAY_RATE * dtSeconds);
        }
      }
    }

    for (const tunnel of this.tunnels.values()) {
      for (const scentType of SCENT_TYPE_ORDER) {
        if (tunnel.scents[scentType] > 0) {
          tunnel.scents[scentType] = Math.max(0, tunnel.scents[scentType] - DECAY_RATE * dtSeconds);
        }
      }
    }
  }

  /**
   * Deposit a scent in a tunnel (e.g., food trail when carrying food).
   */
  depositScent(tunnelId: number, scentType: ScentType, amount: number): void {
    const tunnel = this.tunnels.get(tunnelId);
    if (tunnel && !tunnel.collapsed) {
      tunnel.scents[scentType] = Math.min(1, tunnel.scents[scentType] + amount);
    }
  }

  /**
   * Deposit a scent in a chamber (e.g., alarm pheromone).
   */
  depositScentInChamber(chamberId: number, scentType: ScentType, amount: number): void {
    const chamber = this.chambers.get(chamberId);
    if (chamber && !chamber.destroyed) {
      chamber.scents[scentType] = Math.min(1, chamber.scents[scentType] + amount);
    }
  }

  /**
   * Get scent levels at the other end of a tunnel from a given chamber.
   * Returns the scent levels in the chamber at the far end.
   */
  getScentAtTunnelEnd(tunnelId: number, fromChamberId: number): ScentLevels | null {
    const tunnel = this.tunnels.get(tunnelId);
    if (!tunnel || tunnel.collapsed) return null;

    const otherChamberId = tunnel.connectedChambers.find(id => id !== fromChamberId);
    if (otherChamberId === undefined) return null;

    const otherChamber = this.chambers.get(otherChamberId);
    if (!otherChamber || otherChamber.destroyed) return null;

    return { ...otherChamber.scents };
  }

  /**
   * Get the tunnel scent levels for choosing direction.
   */
  getTunnelScents(tunnelId: number): ScentLevels | null {
    const tunnel = this.tunnels.get(tunnelId);
    if (!tunnel || tunnel.collapsed) return null;
    return { ...tunnel.scents };
  }

  /**
   * Get all non-collapsed tunnels connected to a chamber.
   */
  getConnectedTunnelIds(chamberId: number): number[] {
    const chamber = this.chambers.get(chamberId);
    if (!chamber) return [];
    return chamber.connectedTunnels.filter(tid => {
      const t = this.tunnels.get(tid);
      return t && !t.collapsed;
    });
  }

  /**
   * Get the chamber at the other end of a tunnel.
   */
  getOtherChamber(tunnelId: number, fromChamberId: number): Chamber | null {
    const tunnel = this.tunnels.get(tunnelId);
    if (!tunnel) return null;

    const otherChamberId = tunnel.connectedChambers.find(id => id !== fromChamberId);
    if (otherChamberId === undefined) return null;

    const chamber = this.chambers.get(otherChamberId);
    return chamber && !chamber.destroyed ? chamber : null;
  }

  /**
   * Choose a tunnel based on scent preference using probabilistic selection.
   * Returns tunnel ID or null if no valid tunnels.
   *
   * @param chamberId - Current chamber
   * @param preferredScent - Scent type to follow
   * @param recentTunnels - Tunnels to avoid (recently visited)
   * @param explorationChance - Probability of random choice (0-1)
   */
  chooseTunnelByScent(
    chamberId: number,
    preferredScent: ScentType,
    recentTunnels: number[] = [],
    explorationChance: number = 0.1
  ): number | null {
    const tunnelIds = this.getConnectedTunnelIds(chamberId);
    if (tunnelIds.length === 0) return null;

    // Filter out recently used tunnels (anti-pheromone behavior)
    let availableTunnels = tunnelIds.filter(tid => !recentTunnels.includes(tid));

    // If all tunnels are recent, use all of them
    if (availableTunnels.length === 0) {
      availableTunnels = tunnelIds;
    }

    // Random exploration
    if (this.simRng.chance(explorationChance)) {
      return this.simRng.pick(availableTunnels)!;
    }

    // Calculate weights based on scent at tunnel endpoints
    const weights: { tunnelId: number; weight: number }[] = [];
    let totalWeight = 0;

    for (const tunnelId of availableTunnels) {
      const endScents = this.getScentAtTunnelEnd(tunnelId, chamberId);
      const tunnelScents = this.getTunnelScents(tunnelId);

      // Weight = combination of destination scent + trail scent
      let weight = 0.1; // Base weight (ensures some chance even with no scent)

      if (endScents) {
        weight += endScents[preferredScent] * 2; // Destination scent matters more
      }
      if (tunnelScents) {
        weight += tunnelScents[preferredScent] * 1; // Trail scent adds to preference
      }

      weights.push({ tunnelId, weight });
      totalWeight += weight;
    }

    // Probabilistic selection (roulette wheel)
    let random = this.simRng.next() * totalWeight;
    for (const { tunnelId, weight } of weights) {
      random -= weight;
      if (random <= 0) {
        return tunnelId;
      }
    }

    // Fallback
    return availableTunnels[0];
  }

  /**
   * Get the midpoint position of a tunnel (for ant movement).
   */
  getTunnelMidpoint(tunnelId: number): Position | null {
    const tunnel = this.tunnels.get(tunnelId);
    if (!tunnel) return null;
    return {
      x: (tunnel.start.x + tunnel.end.x) / 2,
      y: (tunnel.start.y + tunnel.end.y) / 2,
    };
  }

  // ======== Congestion Tracking ========

  /**
   * Record an ant entering a tunnel.
   */
  enterTunnel(tunnelId: number): void {
    const tunnel = this.tunnels.get(tunnelId);
    if (tunnel && !tunnel.collapsed) {
      tunnel.currentTraffic++;
    }
  }

  /**
   * Record an ant exiting a tunnel.
   */
  exitTunnel(tunnelId: number): void {
    const tunnel = this.tunnels.get(tunnelId);
    if (tunnel) {
      tunnel.currentTraffic = Math.max(0, tunnel.currentTraffic - 1);
    }
  }

  /**
   * Get the congestion factor for a tunnel.
   * Returns a multiplier >= 1.0, where higher values mean more congestion.
   */
  getCongestionFactor(tunnelId: number): number {
    const tunnel = this.tunnels.get(tunnelId);
    if (!tunnel || tunnel.collapsed) return 1.0;

    const overflow = Math.max(0, tunnel.currentTraffic - tunnel.trafficCapacity);
    const factor = 1 + 0.5 * (overflow / Math.max(1, tunnel.trafficCapacity));
    return Math.min(3.0, factor); // Cap at 3x slowdown
  }

  /**
   * Get the travel time for a tunnel considering congestion.
   * @param tunnelId - Tunnel ID
   * @param baseSpeed - Base ant speed
   * @returns Time in milliseconds to traverse the tunnel
   */
  getTravelTime(tunnelId: number, baseSpeed: number = 1): number {
    const tunnel = this.tunnels.get(tunnelId);
    if (!tunnel) return 0;

    const baseTime = tunnel.length / (baseSpeed * 60); // 60 = base movement rate
    const congestionFactor = this.getCongestionFactor(tunnelId);
    return baseTime * congestionFactor * 1000; // Convert to ms
  }

  /**
   * Get traffic stats for all tunnels (for debug display).
   */
  getTrafficStats(): Map<number, { current: number; capacity: number; congestion: number }> {
    const stats = new Map();
    for (const tunnel of this.tunnels.values()) {
      if (!tunnel.collapsed) {
        stats.set(tunnel.id, {
          current: tunnel.currentTraffic,
          capacity: tunnel.trafficCapacity,
          congestion: this.getCongestionFactor(tunnel.id),
        });
      }
    }
    return stats;
  }

  /**
   * Repair a collapsed tunnel.
   */
  repairTunnel(tunnelId: number): boolean {
    const tunnel = this.tunnels.get(tunnelId);
    if (tunnel && tunnel.collapsed) {
      tunnel.collapsed = false;
      return true;
    }
    return false;
  }

  /**
   * Get all collapsed tunnels.
   */
  getCollapsedTunnels(): TunnelSegment[] {
    return Array.from(this.tunnels.values()).filter(t => t.collapsed);
  }

  /**
   * Rebuild a destroyed chamber.
   */
  rebuildChamber(chamberId: number): boolean {
    const chamber = this.chambers.get(chamberId);
    if (chamber && chamber.destroyed) {
      chamber.destroyed = false;
      // Reset chamber properties
      chamber.foodStored = 0;
      chamber.cleanliness = 0.8;
      chamber.climateQuality = this.computeClimateQuality(chamber);
      return true;
    }
    return false;
  }

  /**
   * Get all destroyed chambers.
   */
  getDestroyedChambers(): Chamber[] {
    return Array.from(this.chambers.values()).filter(c => c.destroyed);
  }

  /**
   * Compute climate quality for a chamber based on depth and type.
   */
  private computeClimateQuality(chamber: Chamber): number {
    const depthBonus = chamber.depth * 0.04;
    const typeBonus = (chamber.type === ChamberType.NURSERY || chamber.type === ChamberType.QUEEN_CHAMBER) ? 0.1 : 0;
    return Math.min(1, 0.7 + depthBonus + typeBonus);
  }

  /**
   * Get a chamber by ID (alias for getChamber).
   */
  getChamberById(id: number): Chamber | undefined {
    return this.chambers.get(id);
  }

  /**
   * Get a tunnel by ID (alias for getTunnel).
   */
  getTunnelById(id: number): TunnelSegment | undefined {
    return this.tunnels.get(id);
  }

  /**
   * Everything about the dig that a season can change.
   *
   * An *overlay*, not the map. The layout — where the chambers are, which tunnels
   * join them, how wide and how deep — is a pure function of the colony index and
   * is regenerated by `generateNetwork()`, so recording it would be recording
   * arithmetic. What a season changes is the contents and the damage: food,
   * eggs, scents, stimuli, collapse, who is standing where.
   *
   * The counts ride along so that restoring into a differently generated network
   * fails loudly instead of silently dropping the chambers it could not find.
   */
  save(): TunnelNetworkSnapshot {
    const chambers = Array.from(this.chambers.values())
      .sort((a, b) => a.id - b.id)
      .map((c) => ({
        id: c.id,
        destroyed: c.destroyed,
        occupantIds: [...c.occupantIds],
        foodStored: c.foodStored,
        foodHue: c.foodHue,
        eggCount: c.eggCount,
        scents: { ...c.scents },
        stimuli: { ...c.stimuli },
        climateQuality: c.climateQuality,
        cleanliness: c.cleanliness,
        riskLevel: c.riskLevel,
      }));

    const tunnels = Array.from(this.tunnels.values())
      .sort((a, b) => a.id - b.id)
      .map((t) => ({
        id: t.id,
        collapsed: t.collapsed,
        pheromoneLevel: t.pheromoneLevel,
        scents: { ...t.scents },
        currentTraffic: t.currentTraffic,
      }));

    return { chamberCount: this.chambers.size, tunnelCount: this.tunnels.size, chambers, tunnels };
  }

  /** Apply an overlay taken by `save()` to a freshly generated network. */
  load(state: TunnelNetworkSnapshot): void {
    if (state.chamberCount !== this.chambers.size || state.tunnelCount !== this.tunnels.size) {
      throw new RangeError(
        `tunnel snapshot describes ${state.chamberCount} chambers and ` +
        `${state.tunnelCount} tunnels; this network has ${this.chambers.size} and ` +
        `${this.tunnels.size}. The layout is generated from the colony index — ` +
        `restoring into the wrong colony is not a recoverable condition`
      );
    }

    for (const saved of state.chambers) {
      const chamber = this.chambers.get(saved.id);
      if (!chamber) throw new RangeError(`tunnel snapshot names no chamber ${saved.id}`);
      chamber.destroyed = saved.destroyed;
      chamber.occupantIds = [...saved.occupantIds];
      chamber.foodStored = saved.foodStored;
      chamber.foodHue = saved.foodHue;
      chamber.eggCount = saved.eggCount;
      chamber.scents = { ...saved.scents };
      chamber.stimuli = { ...saved.stimuli };
      chamber.climateQuality = saved.climateQuality;
      chamber.cleanliness = saved.cleanliness;
      chamber.riskLevel = saved.riskLevel;
    }

    for (const saved of state.tunnels) {
      const tunnel = this.tunnels.get(saved.id);
      if (!tunnel) throw new RangeError(`tunnel snapshot names no tunnel ${saved.id}`);
      tunnel.collapsed = saved.collapsed;
      tunnel.pheromoneLevel = saved.pheromoneLevel;
      tunnel.scents = { ...saved.scents };
      tunnel.currentTraffic = saved.currentTraffic;
    }
  }

  /**
   * Reset the tunnel network to initial state.
   * Clears all chambers and tunnels so generateNetwork() can recreate them.
   */
  reset(): void {
    this.chambers.clear();
    this.tunnels.clear();
    this.spatialGrid.clear();
    this.nextChamberId = 0;
    this.nextTunnelId = 0;
    // Reset the RNG so regenerated network is identical
    this.rng = this.createSeededRandom(this.seed);
  }
}
